import type {HTMLOcTextFieldV1Element} from '@otto-ec/otto-components/text-field'
import type {HTMLOcTextAreaV1Element} from '@otto-ec/otto-components/text-area'

type ValidatableField = HTMLOcTextFieldV1Element | HTMLOcTextAreaV1Element

class ValidatingInputElement {

    private _isValid = true
    private _isVisualError = false
    private _isOptional = false
    private readonly validationChangeCallback: ValidationChangeCallback
    private readonly $inputField: ValidatableField
    private readonly validationConfig: ValidationConfig
    private readonly invalidMessage: string

    private constructor($inputField: ValidatableField, onValidationStateChange: ValidationChangeCallback) {
        this.validationChangeCallback = onValidationStateChange
        this.$inputField = $inputField
        this.validationConfig = this.json2ValidationConfig(this.$inputField.getAttribute('data-js-validation-config') ?? '')
        this.invalidMessage = this.$inputField.getAttribute('data-invalid-message') ?? ''
    }

    public get isValid(): boolean {
        return this._isValid
    }

    public get isOptional(): boolean {
        return this._isOptional
    }

    public setOptional(isOptional: boolean): void {
        this._isOptional = isOptional
        this.validateInput(new Event('input'))
    }

    public revealErrorForSubmit(): boolean {
        if(!this._isValid) {
            this.setVisualError(true)
        }
        return this._isValid
    }

    private setValid(isValid: boolean): void {
        if(this._isValid !== isValid) {
            this._isValid = isValid
            this.validationChangeCallback()
        }
    }

    private setVisualError(isVisualError: boolean): void {
        this._isVisualError = isVisualError
        this.updateValidationMessage()
    }

    private bindValidation(): void {
        this.$inputField.addEventListener('input', (event) => this.validateInput(event))
        this.$inputField.addEventListener('change', (event) => {
            this.$inputField.value = (this.$inputField.value ?? '').trim()
            this.validateInput(event)
        })
    }

    private validateInput(givenEvent: Event): void {
        let isValid = true
        let isVisualError = false
        const inputLength: number = (this.$inputField.value ?? '').length
        const isOptionalAndEmpty = inputLength == 0 && this._isOptional
        const isNotEmpty = inputLength > 0
        const isTooShort = inputLength < this.validationConfig.min
        const isTooLong = inputLength > this.validationConfig.max
        const isOutOfValidRange = !isOptionalAndEmpty && (isTooShort || isTooLong)
        const isNotMatchingPattern = isNotEmpty && this.validationConfig.pattern && !this.validationConfig.pattern.test(this.$inputField.value ?? '')
        if(isOutOfValidRange || isNotMatchingPattern) {
            switch (givenEvent.type) {
                case 'change':
                    isValid = false
                    isVisualError = isNotEmpty
                    break
                default:
                    isValid = false
                    isVisualError = isOutOfValidRange && isTooLong
                    break
            }
        }
        this.setValid(isValid)
        this.setVisualError(isVisualError)
    }

    private json2ValidationConfig(json: string): ValidationConfig {
        const validationConfig = JSON.parse(json || '{"min":1, "max": 1000}')
        if(validationConfig.pattern) {
            validationConfig.pattern = new RegExp(validationConfig.pattern, 'i')
        }
        if(validationConfig.optional && ('boolean' === typeof validationConfig.optional || validationConfig.optional === 'true')) {
            this._isOptional = true
        }
        return validationConfig
    }

    private updateValidationMessage(): void {
        this.$inputField.validationMessage = this._isVisualError ? this.invalidMessage : ''
    }

    static initialize(inputField: ValidatableField | null, onValidationStateChange: ValidationChangeCallback): ValidatingInputElement | null {
        if(inputField == null) {
            return null
        }
        const validatingInputElement = new ValidatingInputElement(inputField, onValidationStateChange)
        validatingInputElement.bindValidation()
        validatingInputElement.validateInput(new Event('input'))
        return validatingInputElement
    }
}

export {ValidatingInputElement as default}

interface ValidationChangeCallback { (): void }
interface ValidationConfig {
    min: number
    max: number
    optional?: boolean
    pattern?: RegExp
}
